import { Navigate, useParams, useNavigate } from '@tanstack/react-router';
import { Button, LoadingIcon, ProductCard, Typography } from '@/primer/components';
import { PageColumn } from '@/components/layout';
import { TrackingCard } from '@/components/ui';
import { useCampaign } from '@/api';
import type {
  CampaignProduct,
  CampaignServiceCampaignDetail,
} from '@bvengineering/cas-member-gateway-service-client';

/** Steps shown under "What's next?" once the creator has claimed their product. */
const WHATS_NEXT_STEPS = [
  "We'll ship your product to you for free. 🚚",
  'Once you get your product(s) you can start creating content. 🤳',
];

/**
 * Resolves the products the creator has committed to for this campaign.
 *
 * A user product counts once it is reserved or claimed — the state transitions
 * reserved → claimed on accept, so both are included to render correctly before
 * and after the campaign-detail query refreshes. Display fields (name, brand,
 * image) are taken from the full product catalogue, falling back to the user
 * product's own fields.
 */
function resolveClaimedProducts(campaign: CampaignServiceCampaignDetail): CampaignProduct[] {
  const productsById = new Map(
    (campaign.products ?? []).map((product) => [product.casProductId, product])
  );

  return (campaign.userProducts ?? [])
    .filter((product) => {
      const state = product.state?.toLowerCase();
      return (
        !!product.claimedDate ||
        !!product.reservedDate ||
        state === 'claimed' ||
        state === 'reserved'
      );
    })
    .map((userProduct) => productsById.get(userProduct.casProductId) ?? userProduct);
}

/**
 * Post-claim confirmation screen in the campaign participation flow.
 *
 * Reached after the creator accepts a campaign. Celebrates the claim, shows the
 * claimed product(s), the shipment tracking code once available, and what to
 * expect next.
 */
export function CampaignClaimSuccess() {
  const { id } = useParams({ from: '/_authenticated/campaign/$id/accepted' });
  const navigate = useNavigate();

  const { data: campaign, isLoading, error } = useCampaign(id);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingIcon size="xl" />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <PageColumn className="py-6 flex flex-col gap-4">
        <Typography variant="header" size="h1" as="h1">
          Unable to load your campaign
        </Typography>
        <Button onClick={() => navigate({ to: '/dashboard' })}>Back to Dashboard</Button>
      </PageColumn>
    );
  }

  const claimedProducts = resolveClaimedProducts(campaign);

  if (claimedProducts.length === 0) {
    return <Navigate to="/campaign/$id/select-product" params={{ id }} replace />;
  }

  const trackingCode = campaign.shipping?.trackingCode;

  return (
    <PageColumn className="py-6 flex flex-col gap-10">
      <div className="flex flex-col gap-4">
        <Typography variant="display" size="medium" as="h1">
          You&rsquo;re in! <span aria-hidden="true">🎁</span>
        </Typography>

        {claimedProducts.map((product) => (
          <ProductCard
            key={product.casProductId}
            imageUrl={product.productImageUrl ?? '/assets/images/placeholder.svg'}
            imageAlt={product.productImageUrl ? (product.productName ?? 'Your product') : ''}
            brandName={product.productBrand ?? ''}
            productName={product.productName ?? 'Your product'}
          />
        ))}

        {trackingCode && (
          <TrackingCard trackingCode={trackingCode} trackingUrl={campaign.shipping?.trackingUrl} />
        )}
      </div>

      <div className="flex flex-col gap-4">
        <Typography variant="header" size="h2" as="h2">
          What&rsquo;s next?
        </Typography>

        <ol className="flex flex-col gap-4 list-none p-0 m-0">
          {WHATS_NEXT_STEPS.map((step, index) => (
            <li key={step} className="flex gap-4">
              <Typography variant="header" size="h2" as="span">
                {index + 1}.
              </Typography>
              <Typography variant="body" size="medium" color="header">
                {step}
              </Typography>
            </li>
          ))}
        </ol>
      </div>

      <div className="flex flex-col items-center gap-3">
        <Typography variant="body" size="medium" color="bodylight" align="center">
          Ready to post your content?
        </Typography>
        {/* Content-creation flow lands in a later ticket; route to the campaign for now. */}
        <Button
          variant="primary"
          fullWidth
          size="large"
          onClick={() => navigate({ to: '/campaign/$id', params: { id } })}
        >
          Yes, let&rsquo;s go!
        </Button>
      </div>
    </PageColumn>
  );
}

export default CampaignClaimSuccess;
